/* ============================================================================
   SUNNY STRIPES — MULTI-SHAPE SATELLITE MEASURING                  v2026-08-31
   ----------------------------------------------------------------------------
   A property is not one shape. A strip plaza is a main lot, an overflow lot
   across a drive aisle, a sidewalk run along the storefronts and a rear service
   area — four disconnected things that have to be measured separately and
   totalled by surface. That is what this file does, and it is deliberately
   generic: it knows about "surfaces you can draw", not about snow.

   It is used by the commercial snow intake today. It is meant to be used by a
   commercial grounds builder next, with turf surfaces instead of pavement, so
   DO NOT put snow rules or snow prices in here.

   THREE WAYS TO MEASURE, because not every surface is a polygon:

     mode "area"   a closed polygon. Right for lots, aprons, service areas.
     mode "path"   a traced centre-line times an assumed width. Right for
                   sidewalks: tracing both edges of a 4 ft walk on a satellite
                   image is fiddly and inaccurate, while clicking along the
                   middle of it is quick and lands within a few percent.
     mode "point"  a dropped marker, counted not measured. Right for entrances
                   and for marking where snow can physically be stacked.

   WHY STACKING IS A SURFACE
     On a commercial lot the question "where does the snow go" changes the job
     more than a few thousand square feet does. Nowhere to stack means hauling,
     which is a different service at a different price. Marking it is the whole
     point of letting someone draw their own property.

   REQUIRES the Google Maps JS API with the `geometry` library. The caller loads
   it; create() throws nothing and simply reports notReady if it is missing.
   ============================================================================ */
(function () {
  "use strict";

  var M2_TO_FT2 = 10.7639;
  var M_TO_FT   = 3.28084;

  function ready() {
    return !!(window.google && google.maps && google.maps.Map &&
              google.maps.geometry && google.maps.geometry.spherical);
  }

  /* THE ONE RULE THAT WILL BITE YOU
     Google measures the container exactly once, at construction, and never
     looks again. A map built inside a display:none panel comes up 0x0 and is
     then dead FOREVER - triggering a resize afterwards does not revive it, and
     nothing is logged anywhere. Tools like this live inside wizard steps, so
     create() refuses to build a map it cannot size and says why. Call it again
     once the panel is on screen; both callers below do exactly that. */
  function hidden(el) { return !el || !el.clientHeight; }

  /* WHEN GOOGLE JUST DOES NOT DRAW
     Seen repeatedly on the live site: the API loads, the Map constructor runs
     without complaint, and then nothing ever renders - no tiles, no idle event,
     no console error. It comes and goes, and a bare `new google.maps.Map` into
     a plain visible div fails the same way, so it is an account-level problem
     (key restrictions, quota, billing) and not something code can fix.
     What code CAN do is refuse to leave a customer staring at a blank grey box
     under an instruction to click the corners of their parking lot. */
  var AUTH_FAILED = false;
  if (!window.gm_authFailure) {
    window.gm_authFailure = function () { AUTH_FAILED = true; };
  }
  var STUCK_AFTER_MS = 9000;

  function create(opts) {
    opts = opts || {};
    if (!ready()) return { notReady: true };
    if (hidden(opts.mapEl)) return { notReady: true, hidden: true };

    var surfaces = opts.surfaces || [];
    var byKey = {};
    surfaces.forEach(function (s) { byKey[s.key] = s; });

    var map = new google.maps.Map(opts.mapEl, {
      center: opts.center || { lat: 43.75, lng: -87.75 },
      zoom: opts.zoom || 19,
      mapTypeId: "satellite",
      tilt: 0,
      disableDefaultUI: true,
      zoomControl: true,
      /* A stray double-click while drawing must not throw the map about. See
         the note by the keyboard handler for what this costs us. */
      disableDoubleClickZoom: true,
      gestureHandling: "greedy"
    });

    var state = {
      surface: surfaces.length ? surfaces[0].key : null,
      shapes: [],        /* every finished shape, in draw order */
      draft: null,       /* the shape being drawn right now, or null */
      seq: 0
    };

    function emit() { if (typeof opts.onChange === "function") opts.onChange(api); }

    /* ---- measuring ------------------------------------------------------ */
    function measureShape(sh) {
      var sp = google.maps.geometry.spherical;
      if (sh.mode === "area") {
        var path = sh.overlay.getPath();
        sh.points = path.getLength();
        sh.sqft = sh.points >= 3 ? Math.round(sp.computeArea(path) * M2_TO_FT2) : 0;
        sh.feet = 0;
      } else if (sh.mode === "path") {
        var p2 = sh.overlay.getPath();
        sh.points = p2.getLength();
        sh.feet = p2.getLength() >= 2 ? Math.round(sp.computeLength(p2) * M_TO_FT) : 0;
        /* A traced centre-line is only a measurement once you say how wide the
           walk is. Width is per-surface and adjustable; 4 ft is the common
           residential-scale walk and a safe default. */
        sh.sqft = Math.round(sh.feet * (byKey[sh.surface].width || 4));
      } else {
        sh.points = 1; sh.sqft = 0; sh.feet = 0;
      }
      return sh;
    }

    function remeasureAll() { state.shapes.forEach(measureShape); emit(); }

    /* ---- drawing -------------------------------------------------------- */
    function styleFor(key) {
      var s = byKey[key] || {};
      return {
        strokeColor: s.color || "#E0A500",
        strokeWeight: s.mode === "path" ? 5 : 3,
        fillColor: s.color || "#E0A500",
        fillOpacity: 0.28
      };
    }

    function startDraft(latLng) {
      var surf = byKey[state.surface];
      if (!surf) return;
      var st = styleFor(state.surface);
      var sh = {
        id: "s" + (++state.seq),
        surface: state.surface,
        mode: surf.mode,
        label: surf.label
      };

      if (surf.mode === "point") {
        sh.overlay = new google.maps.Marker({
          map: map, position: latLng, draggable: true,
          title: surf.label,
          label: { text: (surf.pin || "•"), color: "#fff", fontSize: "13px", fontWeight: "700" }
        });
        sh.overlay.addListener("dragend", function () { emit(); });
        measureShape(sh);
        state.shapes.push(sh);
        emit();
        return;
      }

      if (surf.mode === "area") {
        sh.overlay = new google.maps.Polygon({
          map: map, paths: [latLng], editable: true, draggable: false,
          strokeColor: st.strokeColor, strokeWeight: st.strokeWeight,
          fillColor: st.fillColor, fillOpacity: st.fillOpacity
        });
      } else {
        sh.overlay = new google.maps.Polyline({
          map: map, path: [latLng], editable: true, draggable: false,
          strokeColor: st.strokeColor, strokeWeight: st.strokeWeight, strokeOpacity: 0.95
        });
      }

      var p = sh.overlay.getPath();
      ["set_at", "insert_at", "remove_at"].forEach(function (ev) {
        google.maps.event.addListener(p, ev, function () { measureShape(sh); emit(); });
      });

      state.draft = sh;
      measureShape(sh);
      emit();
    }

    function addVertex(latLng) {
      if (!state.draft) return startDraft(latLng);
      state.draft.overlay.getPath().push(latLng);
      measureShape(state.draft);
      emit();
    }

    /* A shape is only worth keeping if it has enough points to have a size. */
    function finishDraft() {
      var sh = state.draft;
      if (!sh) return false;
      var need = sh.mode === "area" ? 3 : 2;
      if (sh.overlay.getPath().getLength() < need) return false;
      measureShape(sh);
      state.shapes.push(sh);
      state.draft = null;
      emit();
      return true;
    }

    function cancelDraft() {
      if (!state.draft) return;
      state.draft.overlay.setMap(null);
      state.draft = null;
      emit();
    }

    map.addListener("click", function (ev) { addVertex(ev.latLng); });

    /* Enter closes the shape, Escape throws the draft away. Tested on the live
       page: double-click does NOT work as a closing gesture here, because
       disableDoubleClickZoom (which we want, so a stray double-click mid-draw
       does not throw the map about) also stops Maps firing the dblclick event.
       The Finish shape button in the hint bar is the visible way to do this;
       these are for the people who never look at buttons. */
    function keys(e) {
      if (!state.draft) return;
      /* Never steal Enter from someone typing in the sidewalk width or any
         other field on the page. */
      var tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Enter") { e.preventDefault(); finishDraft(); }
      else if (e.key === "Escape") { e.preventDefault(); cancelDraft(); }
    }
    document.addEventListener("keydown", keys);

    /* ---- totals --------------------------------------------------------- */
    function totals() {
      var out = {};
      surfaces.forEach(function (s) {
        out[s.key] = { key: s.key, label: s.label, mode: s.mode, count: 0, sqft: 0, feet: 0 };
      });
      state.shapes.forEach(function (sh) {
        var t = out[sh.surface];
        if (!t) return;
        t.count += 1;
        t.sqft  += sh.sqft || 0;
        t.feet  += sh.feet || 0;
      });
      return out;
    }

    function isEmpty() { return state.shapes.length === 0; }

    /* A one-line-per-surface plain-text block for the lead email. */
    function summaryLines() {
      var t = totals(), lines = [];
      surfaces.forEach(function (s) {
        var row = t[s.key];
        if (!row.count) return;
        if (s.mode === "area") {
          lines.push(row.label + ": " + row.sqft.toLocaleString("en-US") + " sq ft across " +
                     row.count + (row.count === 1 ? " area" : " areas"));
        } else if (s.mode === "path") {
          lines.push(row.label + ": " + row.feet.toLocaleString("en-US") + " linear ft (~" +
                     row.sqft.toLocaleString("en-US") + " sq ft at " + (s.width || 4) + " ft wide) across " +
                     row.count + (row.count === 1 ? " run" : " runs"));
        } else {
          lines.push(row.label + ": " + row.count + " marked");
        }
      });
      return lines;
    }

    var api = {
      notReady: false,
      stuck: false,        /* set if Google never draws - see the note above */
      map: map,
      surfaces: surfaces,
      /* what the next click will draw */
      setSurface: function (k) { if (byKey[k]) { cancelDraft(); state.surface = k; emit(); } },
      currentSurface: function () { return state.surface; },
      drafting: function () { return !!state.draft; },
      draftPoints: function () { return state.draft ? state.draft.overlay.getPath().getLength() : 0; },
      finish: finishDraft,
      cancel: cancelDraft,
      shapes: function () { return state.shapes.slice(); },
      remove: function (id) {
        for (var i = 0; i < state.shapes.length; i++) {
          if (state.shapes[i].id === id) {
            state.shapes[i].overlay.setMap(null);
            state.shapes.splice(i, 1);
            emit();
            return true;
          }
        }
        return false;
      },
      undo: function () {
        if (state.draft) { cancelDraft(); return true; }
        var last = state.shapes.pop();
        if (!last) return false;
        last.overlay.setMap(null);
        emit();
        return true;
      },
      clear: function () {
        cancelDraft();
        state.shapes.forEach(function (sh) { sh.overlay.setMap(null); });
        state.shapes = [];
        emit();
      },
      /* Changing a walk's assumed width re-measures every path already drawn. */
      setWidth: function (key, ft) {
        if (!byKey[key]) return;
        byKey[key].width = ft;
        remeasureAll();
      },
      widthOf: function (key) { return (byKey[key] && byKey[key].width) || 4; },
      totals: totals,
      isEmpty: isEmpty,
      summaryLines: summaryLines,
      /* A live map that gets hidden and shown again lays out fine once you tell
         it to. (A map BORN hidden does not - see the note above create().) */
      invalidate: function (latLng, zoom) {
        google.maps.event.trigger(map, "resize");
        if (latLng) map.setCenter(latLng);
        if (zoom) map.setZoom(zoom);
      },
      recenter: function (latLng, zoom) {
        if (!latLng) return;
        map.setCenter(latLng);
        if (zoom) map.setZoom(zoom);
      }
    };

    /* If no tile ever arrives, stand the tool down rather than lie to the
       visitor. api.stuck tells the caller to hide its drawing controls. */
    var drew = false;
    google.maps.event.addListenerOnce(map, "tilesloaded", function () { drew = true; });
    setTimeout(function () {
      if (drew || api.stuck) return;
      api.stuck = true;
      opts.mapEl.innerHTML =
        '<div style="height:100%;display:flex;align-items:center;justify-content:center;' +
        'text-align:center;padding:22px;font-size:13px;line-height:1.5;color:#5b6b5e">' +
        "<span><b>The satellite map isn&rsquo;t loading right now.</b><br>" +
        "Nothing you did &mdash; carry on without it and we&rsquo;ll measure the " +
        "property when we walk the site.</span></div>";
      if (typeof opts.onStuck === "function") opts.onStuck(AUTH_FAILED);
      emit();
    }, STUCK_AFTER_MS);

    emit();
    return api;
  }

  window.SS_MEASURE = { ready: ready, create: create };
})();
